/**
 * Sound Effects Utility
 * Generates and plays subtle sound effects using Web Audio API
 */

let audioContext: AudioContext | null = null;
let waitingSoundInterval: number | null = null;

/**
 * Initialize AudioContext (requires user interaction)
 */
function initAudioContext(): AudioContext | null {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (err) {
      console.warn('[soundEffects] Web Audio API not supported:', err);
      return null;
    }
  }
  
  // Resume AudioContext if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch((err) => {
      console.warn('[soundEffects] Failed to resume AudioContext:', err);
    });
  }
  
  return audioContext;
}

/**
 * Play a tone using Web Audio API
 */
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3
): void {
  const ctx = initAudioContext();
  if (!ctx) {
    return; // Gracefully degrade if Web Audio API unavailable
  }

  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;

    // Fade in/out for smooth sound
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

    oscillator.start(now);
    oscillator.stop(now + duration);
  } catch (err) {
    console.warn('[soundEffects] Failed to play tone:', err);
  }
}

/**
 * Play waiting sound (subtle tone while AI is processing)
 * Frequency: 220 Hz (A3), Triangle wave, 0.2s duration, 15% volume
 */
export function playWaitingSound(): void {
  playTone(220, 0.2, 'triangle', 0.15);
}

/**
 * Play interrupt sound (when stopping AI audio)
 * Frequency: 200 Hz, Sine wave, 0.15s duration, 30% volume
 */
export function playInterruptSound(): void {
  playTone(200, 0.15, 'sine', 0.3);
}

/**
 * Stop waiting sound (cleanup function)
 * Currently waiting sound plays once, but kept for future looping implementation
 */
export function stopWaitingSound(): void {
  if (waitingSoundInterval !== null) {
    clearInterval(waitingSoundInterval);
    waitingSoundInterval = null;
  }
}

